/**
 * Onboarding flow — screen taxonomy.
 *
 * The wizard's full ordered screen list. Each data-bearing screen is a
 * single component that handles entry + review + the persistence call —
 * no separate "review" screens, no API request until the user confirms.
 *
 * The hook derives which screen to drop a partially-onboarded user back
 * into. Non-data screens (`GREAT_START`, `ALMOST_THERE`, `PHONE_NUMBER_VERIFY`,
 * `PAYMENT`, plus `DEPENDANT_ENTRY`/`DEPENDANT_REVIEW`, which share the
 * dependant-count gate's signal) are part of the wizard sequence but can never
 * be a derive target. The optional loved-ones screens (`DEPENDANT_RESIDENCE`,
 * `DEPENDANT_VEHICLE`) are derive targets via frontier resume — see
 * {@link deriveOnboardingScreen}. A fully-onboarded user gets
 * `null` instead of a screen.
 *
 * "Onboarded" is derived from data presence on the family-scoped tables
 * (dependants, caregiver relationships, partnerships, financial accounts) +
 * `phoneNumberVerifiedAt`. There is no `onboarding_progress` row anymore;
 * see [ORN-575](https://linear.app/go-mantle/issue/ORN-575) for the pivot.
 *
 * For the four-phase visual grouping rendered by `OnboardingPhaseStepper`
 * (which also includes the two `PreOnboardingScreen` values that precede this
 * enum), see `./wizardPhases.ts`.
 */

export enum OnboardingScreen {
  PHONE_NUMBER = 'PHONE_NUMBER',
  PHONE_NUMBER_VERIFY = 'PHONE_NUMBER_VERIFY',
  USER_RESIDENCE = 'USER_RESIDENCE',
  GREAT_START = 'GREAT_START',
  DEPENDANT_COUNT = 'DEPENDANT_COUNT',
  DEPENDANT_ENTRY = 'DEPENDANT_ENTRY',
  DEPENDANT_REVIEW = 'DEPENDANT_REVIEW',
  DEPENDANT_RESIDENCE = 'DEPENDANT_RESIDENCE',
  DEPENDANT_VEHICLE = 'DEPENDANT_VEHICLE',
  ALMOST_THERE = 'ALMOST_THERE',
  PAYMENT = 'PAYMENT',
  FINANCIAL_ACCT_CONNECT = 'FINANCIAL_ACCT_CONNECT',
}

/**
 * Linear render order for the wizard. The index doubles as a "progress
 * percentage" denominator and as the source of truth for next / previous
 * navigation.
 */
export const ONBOARDING_SCREEN_ORDER: readonly OnboardingScreen[] = [
  OnboardingScreen.PHONE_NUMBER,
  OnboardingScreen.PHONE_NUMBER_VERIFY,
  OnboardingScreen.USER_RESIDENCE,
  OnboardingScreen.GREAT_START,
  OnboardingScreen.DEPENDANT_COUNT,
  OnboardingScreen.DEPENDANT_ENTRY,
  OnboardingScreen.DEPENDANT_REVIEW,
  OnboardingScreen.DEPENDANT_RESIDENCE,
  OnboardingScreen.DEPENDANT_VEHICLE,
  OnboardingScreen.ALMOST_THERE,
  OnboardingScreen.FINANCIAL_ACCT_CONNECT,
  OnboardingScreen.PAYMENT,
] as const;

/**
 * Returns the next screen in `ONBOARDING_SCREEN_ORDER`, or `null` if `current`
 * is the terminal screen.
 */
export function getNextScreen(current: OnboardingScreen): OnboardingScreen | null {
  const index = ONBOARDING_SCREEN_ORDER.indexOf(current);
  return index === -1 || index === ONBOARDING_SCREEN_ORDER.length - 1 ? null : ONBOARDING_SCREEN_ORDER[index + 1];
}

/**
 * Returns the previous screen, or `null` if `current` is the first screen.
 */
export function getPreviousScreen(current: OnboardingScreen): OnboardingScreen | null {
  const index = ONBOARDING_SCREEN_ORDER.indexOf(current);
  return index <= 0 ? null : ONBOARDING_SCREEN_ORDER[index - 1];
}

/**
 * Screens past the loved-ones phase, reachable only by an account with alpha
 * access. A waitlisted account works through the wizard and stops here: the
 * flow renders `WaitlistedStep` in place of the first of these rather than
 * asking for bank credentials or a subscription it can't act on.
 *
 * Two callers share the rule — {@link getNextScreen}'s forward walk and the
 * `/onboarding` route decision, which reads it against the resumed screen.
 */
export function isAlphaGatedScreen(screen: OnboardingScreen): boolean {
  return (
    screen === OnboardingScreen.ALMOST_THERE ||
    screen === OnboardingScreen.FINANCIAL_ACCT_CONNECT ||
    screen === OnboardingScreen.PAYMENT
  );
}

/** True when `current` is the last screen in the order. */
export function isTerminalScreen(current: OnboardingScreen): boolean {
  return current === ONBOARDING_SCREEN_ORDER[ONBOARDING_SCREEN_ORDER.length - 1];
}
